import React from 'react'
import styled from "styled-components";

// CONTENT COMPONENTS
import { device } from "../../utils";
import theme from "../../utils/theme";
import ItemMedia from './ItemMedia';
import ItemDescription from './ItemDescription';
import ItemProperties from './ItemProperties';
import ItemOptions from './ItemOptions';
import ShopifyBuyButton from './ShopifyBuyButton';
import CenterEye from '../TheEye/CenterEye';



// ~~~~ STYLING
const ShopGalleryItemWrap = styled.div`
  position: relative;
  overflow: visible;
  width: 100%;
  margin-bottom: 60px;
  display: inline-flex;
  flex-wrap: wrap;

  /* DESKTOP : one item per row (media + spec panel side by side inside),
     two per row once there's room for two full spreads */
  @media (min-width: 1540px) {
    width: calc( 50% - 10px );
  }


  // ITEM HEADER : the item's title above the card — same inverted bar as
  // the desktop pane-title: palette fill, black text, full item width
  .item-header {
    position: absolute;
    top: -32px;
    left: 0;
    width: 100%;
    padding: 3px 15px 5px;
    font-weight: bold;
    font-size: 18px;
    @media (max-width: 767px) { font-size: 16px; }
    background: ${theme.cultPalette[1]};
    color: #000;
    text-shadow: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    .item-header-text {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .item-header-eye {
      flex: 0 0 auto;
      transform: none;
      width: 50px;
      margin-top: -1px;

      // HID IT FOR NOW 
      display: none;
    }
  }


  // CONTENT PANE CONTROLS
  .product-content-pane {
    transition: opacity 0.5s ease-in-out;
  }

  /* MOBILE : original behavior — the Details button toggles between the
     main pane and the spec pane overlaid on top of it */
  @media (max-width: 767px) {
    .product-content-pane {
      &.content-main {
        opacity: 1;
        pointer-events: all;
      }

      &.content-info {
        position: absolute;
        top: 0;
        left: 0;
        height: calc( 100% - 80px );
        opacity: 0;
        pointer-events: none;
        overflow: auto;
      }
    }

    &.specs-view .product-content-pane {
      &.content-main {
        opacity: 0;
        pointer-events: none;
      }
      &.content-info {
        opacity: 1;
        pointer-events: all;
      }
    }
  }

  /* DESKTOP : both panes at once — media on the left, spec sheet on the
     right (ghostline order-panel layout); no toggling */
  @media ${device.md} {
    /* Preview props duplicate the spec sheet on the right — hide here */
    .preview-props {
      display: none;
    }

    /* Title moves into the top of the spec pane (see ItemDescription) */
    .item-header {
      display: none;
    }

    .product-content-pane {
      &.content-main {
        width: 50%;
        /* The right half belongs to the absolute spec pane — push the
           in-flow rows (options, buttons) down to the next line */
        margin-right: 50%;
      }

      &.content-info {
        /* Out of the flex flow entirely: pinned to the card's right half,
           spanning the FULL card height (media + options + buttons rows),
           with the sheet scrolling inside */
        position: absolute;
        top: 5px;
        right: 20px;
        bottom: 5px;
        width: calc(50% - 20px);

        > * {
          position: absolute;
          top: 15px;
          right: 0;
          bottom: 0;
          left: 20px;
          overflow-y: auto;
        }
      }
    }

    /* Options + buy button stay inside the left column (div-qualified so
       this outranks the base .action-button-wrap width further down) */
    div.item-options,
    div.action-button-wrap {
      width: 50%;
      margin-right: 50%;
    }

    /* Both panes always show here — the Details toggle is meaningless.
       Compound selector so it outranks .action-button's display rule below */
    .action-button.details-toggle {
      display: none;
    }

    /* With the toggle gone the buy / notify button rides alone — center it */
    div.action-button-wrap {
      justify-content: center;
    }

    .action-button-wrap .action-button {
      max-width: 100%;
    }
  }

  @media ${device.lg} {
    .product-content-pane {
      &.content-main {
        // width: 60%;
      }

      &.content-info {
        // width: 40%;
      }
    }
  }



  // GALLERY SHOW NOTE : rides just under the price
  .gallery-soon-note {
    margin: 5px 0 15px;
    font-size: 16px;
    @media (max-width: 767px) { font-size: 12px; }
    letter-spacing: 1px;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.7);
    pointer-events: none;
  }

  // PRICE
  .item-price {
    margin-bottom: 15px;
    font-size: 24px;
    @media (max-width: 767px) { font-size: 22px; }
    pointer-events: none;
  }

  // ITEM PROPERTIES
  .item-props {
    text-align: left;
    font-size: 15px;
    @media (max-width: 767px) { font-size: 13px; }
    display: flex;
    flex-wrap: wrap;
  }

    .prop-item {
      width: 100%;
      display: inline-flex;
      align-items: center;
      border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      justify-content: space-between;
      padding: 0;

      &:last-child {
        border-bottom-width: 0px;
      }
    }
  }


  // IMAGERY
  .item-media-wrap {
    padding-top: 15px;
  }

  // FORGE BUTTON
  .action-button-wrap {
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: auto;
    align-self: flex-end;

    .action-button {
      max-width: 47%;
    }
  }
  .action-button {
    margin-bottom: 15px;
    margin-top: 15px;
    padding: 5px 10px;
    width: 100%;
    font-size: 13px;
    @media (max-width: 767px) { font-size: 11px; }
    display: inline-flex;
    align-items: center;
    justify-content: center;

    @media ${device.md} {
      font-size: 16px;
    }

    @media ${device.lg} {
      font-size: 16px;
    }

    @media (min-width:1400px) {
      font-size: 20px;
    }
  }
`;


class ShopGalleryItem extends React.Component {
  constructor(props) {
    super(props);
    const {product} = props;
    const selectionId = product?.variants?.length > 1 ? null : product.variant_id

    this.state = {
      showSpecs: false,
      selectedVariantId: selectionId
    }

    this.toggleSpecs = this.toggleSpecs.bind(this);
    this.selectOption = this.selectOption.bind(this);
  }

  componentDidUpdate(prevProps, prevState) {
    const { showSpecs } = this.state;
    const selection = this.props?.product?.id;
    const prevShowSpecs = prevState.showSpecs;

    // If specs shown, and previously was not shown, scroll to its top for user
    if ( !!showSpecs && !prevShowSpecs ) {
      this.resetContentScroll(selection);
    }
  }

  resetContentScroll(selection) {
    const scrollBox = document.getElementById( 'main-content' );
    const scrollTarget = document.getElementById( selection );
    if ( !!scrollBox && !!scrollBox.scrollTo && !!scrollTarget ) {
      scrollBox.scrollTo(0, 0);
      const targetLocation = scrollTarget.getBoundingClientRect();
      const parentLocation = scrollBox.getBoundingClientRect();
      const distanceToScroll = targetLocation.top - parentLocation.top - 30;
      // console.log( `selection top = ${targetLocation.top} \ntarget top    = ${parentLocation.height} \nscroll amount = ${distanceToScroll}` );
      scrollBox.scrollTo(0, distanceToScroll);
    } 
  }

  toggleSpecs() {
    this.setState({
      showSpecs: !this.state.showSpecs
    })
  }

  // OPTIONS : when an item has sizes, colors or something else
  selectOption(e) {
    const { id } = e.target;
    if ( id !== this.state.selectedVariantId ) {
      this.setState({ selectedVariantId: id });
    }
  }


  render() {
    const { product } = this.props;
    const { selectedVariantId, showSpecs } = this.state;

    // EYE : outer ring is normally the cult blue, but when the header
    // itself landed on blue, swap the outer ring to purple so it doesn't
    // blend into the middle ring (which always matches the header)
    const eyeOuterColor = theme.cultPalette[1] === '#13FFED' ? '#950AFF' : '#13FFED';


    // DETAIL + BUY BUTTONS
    let actionButton = (
      <div className="action-button-wrap">
        <a
          className="action-button cult-button details-toggle"
          onClick={this.toggleSpecs}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') this.toggleSpecs(); }}
          role="button"
          tabIndex={0}
        >{ showSpecs ? 'Back' : 'Details'}</a>
        <ShopifyBuyButton
          product={product}
          selectedVariantId={selectedVariantId}>
        </ShopifyBuyButton>
      </div>
    )


// ~~~~ COMPONENT BODY
    return (
      <ShopGalleryItemWrap id={product?.id} className={`cult-panel ${showSpecs ? 'specs-view':''}`}>

        {/* HEADER : item title in its own box above the card */
          !!product.title && (
            <div className="item-header cult-panel-colors">
              <span className="item-header-text">{product.title}</span>
              <CenterEye
                className="item-header-eye"
                width={22}
                animated={false}
                bodyColor={eyeOuterColor}
                pupilColor={theme.cultPalette[1]}
                irisColor="#000000"
              />
            </div> )}


        {/* CONTENT 1 (of 2) - Main Image + Stats */}
        <div className="content-main product-content-pane">

          {/* MEDIA */}
          <ItemMedia className="item-media-wrap" product={product} />

          {/* PRICE (title lives in the header box above) */
            product.tags.indexOf('gallery-soon') == -1 && (
              <div className="item-price">${product.price}</div>)
          }
          

          {/* GALLERY SHOW NOTE */
            product.tags.indexOf('gallery-soon') > -1 && (
              <div className="gallery-soon-note">Available for purchase during next gallery show</div> )}

          {/* PROPERTIES (preview — hidden on desktop where the full spec
              sheet is already visible alongside) */}
          <ItemProperties className="preview-props" productTags={product.tags} mode={'preview'} />
        </div>


        {/* CONTENT 2 (of 2) - DETAIL DESCRIPTIONS */}
        <div id={`content-info-${product?.id}`} className="content-info product-content-pane">

            {/* DESCRIPTION */}
            <ItemDescription description={product.body_html} title={product.title} productTags={product.tags} />
        </div>


        {/* FORGE BUTTON */}
        <ItemOptions className="item-options" variants={product.variants} selectedVariantId={selectedVariantId} selectOption={this.selectOption} />
        {actionButton}
      </ShopGalleryItemWrap>
    )
  }
};

export default ShopGalleryItem;
